import React, {useState, useRef, useEffect} from "react";
import {useForm} from "react-hook-form";
import {toast} from "react-toastify";
import {BeatLoader} from "react-spinners";
import {documentAPI, thirdPartyProviderAPI} from "../../../Api/ifex";
import {FiX, FiUpload, FiFile, FiCheck, FiAlertCircle} from "react-icons/fi";

const DocumentUploadModal = ({visible, onClose, onSuccess, user, modules}) => {
    const [uploading, setUploading] = useState(false);
    const [dragActive, setDragActive] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [scannedPlaceholders, setScannedPlaceholders] = useState([]);
    const [uploadComplete, setUploadComplete] = useState(false);
    const [thirdPartyProviders, setThirdPartyProviders] = useState([]);
    const fileInputRef = useRef(null);

    const {register, handleSubmit, watch, setValue, formState: {errors, isValid}} = useForm({
        mode: "onChange",
        defaultValues: {
            document_name: '',
            is_active: '1',
            module: '',
            third_party_provider_id: '',
            third_party_variant: '',
            sequence: '',
            remarks: '',
        }
    });

    useEffect(() => {
        if (visible) {
            fetchThirdPartyProviders();
        }
    }, [visible]);

    const fetchThirdPartyProviders = async () => {
        try {
            const response = await thirdPartyProviderAPI.getAll(true);
            if (response.data.success) {
                setThirdPartyProviders(response.data.providers || []);
            }
        } catch (error) {
            console.error('Error fetching third-party providers:', error);
        }
    };

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            // Only deactivate if leaving the drop zone entirely (not child elements)
            const rect = e.currentTarget.getBoundingClientRect();
            if (
                e.clientX < rect.left ||
                e.clientX >= rect.right ||
                e.clientY < rect.top ||
                e.clientY >= rect.bottom
            ) {
                setDragActive(false);
            }
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleFileChange(e.dataTransfer.files[0]);
        }
    };

    const handleFileInputChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            handleFileChange(e.target.files[0]);
        }
    };

    const handleFileChange = (file) => {
        if (!file.name.endsWith('.docx')) {
            toast.error('Nur .docx Dateien sind erlaubt');
            return;
        }

        setSelectedFile(file);

        // Auto-fill document name if empty
        const currentName = watch('document_name');
        if (!currentName) {
            const nameWithoutExt = file.name.replace('.docx', '');
            setValue('document_name', nameWithoutExt);
        }
    };

    const onSubmit = async (data) => {
        if (!selectedFile) {
            toast.error('Bitte wählen Sie eine Datei aus');
            return;
        }

        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', selectedFile);
            formData.append('document_name', data.document_name);
            formData.append('is_active', data.is_active === '1');
            formData.append('module', data.module);
            formData.append('sequence', data.sequence || '');
            formData.append('remarks', data.remarks || '');
            formData.append('uploaded_by', user.name || user.email);

            const response = await documentAPI.upload(formData);

            if (response.data.success) {
                // Extract scanned placeholders from response
                if (response.data.data.placeholders) {
                    setScannedPlaceholders(response.data.data.placeholders);
                }

                setUploadComplete(true);
                toast.success('Dokument erfolgreich hochgeladen');

                // Auto-close after 2 seconds
                setTimeout(() => {
                    onSuccess();
                }, 2000);
            }
        } catch (error) {
            console.error('Error uploading document:', error);
            if (error.response?.data?.errors) {
                Object.values(error.response.data.errors).forEach(err => {
                    toast.error(err[0]);
                });
            } else {
                toast.error('Fehler beim Hochladen des Dokuments');
            }
        } finally {
            setUploading(false);
        }
    };

    if (!visible) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div
                className="absolute inset-0 bg-modalBackDropColor bg-opacity-60"
                onClick={onClose}
            ></div>
            <div className="relative z-10 bg-white rounded-xl shadow-2xl w-11/12 max-w-4xl max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex justify-between items-center">
                    <h2 className="text-2xl font-bold text-gray-800">Neues Dokument hochladen</h2>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                        <FiX size={24} />
                    </button>
                </div>

                {/* Success State */}
                {uploadComplete ? (
                    <div className="p-10 text-center">
                        <div className="mb-4 flex justify-center">
                            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                                <FiCheck className="text-green-600" size={32} />
                            </div>
                        </div>
                        <h3 className="text-2xl font-bold text-gray-800 mb-2">Upload erfolgreich!</h3>
                        <p className="text-gray-600 mb-4">
                            Das Dokument wurde erfolgreich hochgeladen und {scannedPlaceholders.length} Platzhalter wurden erkannt.
                        </p>

                        {scannedPlaceholders.length > 0 && (
                            <div className="mt-6 p-4 bg-gray-50 rounded">
                                <h4 className="font-semibold text-sm text-gray-700 mb-2">
                                    Erkannte Platzhalter:
                                </h4>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-40 overflow-y-auto">
                                    {scannedPlaceholders.map((ph, idx) => (
                                        <div
                                            key={idx}
                                            className="px-2 py-1 bg-white border border-gray-200 rounded text-xs font-mono"
                                        >
                                            {ph.placeholder_name || ph}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <button
                            onClick={onSuccess}
                            className="mt-6 bg-mainBlue text-white px-6 py-2 rounded hover:bg-blue-700"
                        >
                            Schließen
                        </button>
                    </div>
                ) : (
                    /* Upload Form */
                    <form onSubmit={handleSubmit(onSubmit)} className="p-6">
                        {/* File Upload Area */}
                        <div className="mb-6">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Dokument-Datei * (.docx)
                            </label>
                            <div
                                className={`relative border-2 border-dashed rounded-lg p-8 text-center ${
                                    dragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
                                } ${selectedFile ? 'bg-green-50 border-green-500' : ''}`}
                                onDragEnter={handleDrag}
                                onDragLeave={handleDrag}
                                onDragOver={handleDrag}
                                onDrop={handleDrop}
                            >
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".docx"
                                    onChange={handleFileInputChange}
                                    className="hidden"
                                />

                                {selectedFile ? (
                                    <div className="flex flex-col items-center">
                                        <FiFile className="text-green-600 mb-2" size={48} />
                                        <p className="text-sm font-semibold text-green-700 mb-1">
                                            {selectedFile.name}
                                        </p>
                                        <p className="text-xs text-gray-500 mb-3">
                                            {(selectedFile.size / 1024).toFixed(2)} KB
                                        </p>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setSelectedFile(null);
                                                if (fileInputRef.current) fileInputRef.current.value = '';
                                            }}
                                            className="text-sm text-red-600 hover:text-red-800"
                                        >
                                            Datei entfernen
                                        </button>
                                    </div>
                                ) : (
                                    <div>
                                        <FiUpload className="mx-auto text-gray-400 mb-3" size={48} />
                                        <p className="text-gray-600 mb-2">
                                            Ziehen Sie eine .docx Datei hierher oder
                                        </p>
                                        <button
                                            type="button"
                                            onClick={() => fileInputRef.current?.click()}
                                            className="bg-mainBlue text-white px-4 py-2 rounded hover:bg-blue-700"
                                        >
                                            Datei auswählen
                                        </button>
                                        <p className="text-xs text-gray-500 mt-2">
                                            Nur Word-Dokumente (.docx) erlaubt
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Form Fields */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Document Name */}
                            <div className="md:col-span-2">
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Dokumentname *
                                </label>
                                <input
                                    type="text"
                                    className={`w-full px-3 py-2 border rounded ${errors.document_name ? 'border-red-500' : 'border-gray-300'}`}
                                    placeholder="z.B. DLV-Standard-Vorlage"
                                    {...register('document_name', {
                                        required: 'Dokumentname ist erforderlich'
                                    })}
                                />
                                {errors.document_name && <p className="text-red-500 text-sm mt-1">{errors.document_name.message}</p>}
                            </div>

                            {/* Active Status */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Status *
                                </label>
                                <select
                                    className="w-full px-3 py-2 border border-gray-300 rounded"
                                    {...register('is_active', {required: true})}
                                >
                                    <option value="1">Aktiv</option>
                                    <option value="0">Inaktiv</option>
                                </select>
                            </div>

                            {/* Module */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Modul *
                                </label>
                                <select
                                    className={`w-full px-3 py-2 border rounded ${errors.module ? 'border-red-500' : 'border-gray-300'}`}
                                    {...register('module', {required: 'Modul ist erforderlich'})}
                                >
                                    <option value="">-- Wählen --</option>
                                    {modules.map((module, idx) => (
                                        <option key={idx} value={module}>{module}</option>
                                    ))}
                                </select>
                                {errors.module && <p className="text-red-500 text-sm mt-1">{errors.module.message}</p>}
                            </div>

                            {/* Third-Party Provider */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Drittpartei
                                </label>
                                <select
                                    className="w-full px-3 py-2 border border-gray-300 rounded"
                                    {...register('third_party_provider_id')}
                                >
                                    <option value="">-- Standard (Intern) --</option>
                                    {thirdPartyProviders.map((provider) => (
                                        <option key={provider.id} value={provider.id}>
                                            {provider.provider_name}
                                        </option>
                                    ))}
                                </select>
                                <p className="text-xs text-gray-500 mt-1">Optional: Drittpartei-Anbieter wählen</p>
                            </div>

                            {/* Third-Party Variant */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Variante
                                </label>
                                <input
                                    type="text"
                                    className="w-full px-3 py-2 border border-gray-300 rounded"
                                    placeholder="z.B. Version A, Standard"
                                    {...register('third_party_variant')}
                                />
                                <p className="text-xs text-gray-500 mt-1">Optional: Spezifische Variante</p>
                            </div>

                            {/* Sequence */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Sequenz
                                </label>
                                <input
                                    type="text"
                                    className="w-full px-3 py-2 border border-gray-300 rounded"
                                    placeholder="z.B. 1, 2, 3 oder A, B, C"
                                    {...register('sequence')}
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    Bestimmt die Reihenfolge beim PDF-Merge
                                </p>
                            </div>

                            {/* Remarks */}
                            <div className="md:col-span-2">
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Bemerkungen
                                </label>
                                <textarea
                                    className="w-full px-3 py-2 border border-gray-300 rounded"
                                    rows="3"
                                    placeholder="Optionale Anmerkungen zum Dokument"
                                    {...register('remarks')}
                                />
                            </div>
                        </div>

                        {/* Info Box */}
                        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded">
                            <div className="flex items-start gap-2">
                                <FiAlertCircle className="text-blue-600 mt-0.5 flex-shrink-0" />
                                <div className="text-sm text-blue-800">
                                    <p className="font-semibold mb-1">Hinweis:</p>
                                    <p>
                                        Nach dem Upload wird das Dokument automatisch nach Platzhaltern gescannt.
                                        Alle erkannten Platzhalter werden angezeigt und können später zugeordnet werden.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
                                disabled={uploading}
                            >
                                Abbrechen
                            </button>
                            <button
                                type="submit"
                                disabled={uploading || !isValid || !selectedFile}
                                className={`px-4 py-2 rounded text-white flex items-center gap-2 ${
                                    uploading || !isValid || !selectedFile ? 'bg-mainBlue cursor-not-allowed opacity-60' : 'bg-mainBlue hover:bg-blue-700'
                                }`}
                            >
                                {uploading && <BeatLoader size={8} color="#ffffff" />}
                                {uploading ? 'Lade hoch...' : 'Hochladen & Scannen'}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default DocumentUploadModal;
